import React from 'react';
import config from '../../config/config';
import { BaseAdProps, AdSlot } from './types';
import AdSenseAd from './providers/adsense/AdSenseAd';
import EzoicAd from './providers/ezoic/EzoicAd';
import { useAdSense } from './providers/adsense/AdSenseProvider';
import { useEzoic } from './providers/ezoic/EzoicProvider';

/**
 * SmartAd - Unified ad component that works with any provider
 * Automatically routes to the correct provider based on configuration
 */
const SmartAd: React.FC<BaseAdProps> = ({
  slot,
  enabled = true,
  adFormat = 'auto',
  style,
  className,
  fallbackContent,
}) => {
  const provider = config.ads.provider;

  // Don't render anything if ads are disabled
  if (!enabled) {
    return fallbackContent ? <>{fallbackContent}</> : null;
  }

  // Route to AdSense provider
  if (provider === 'adsense') {
    return <AdSenseSmartAd 
      slot={slot} 
      adFormat={adFormat} 
      style={style} 
      className={className} 
    />;
  }

  // Route to Ezoic provider
  if (provider === 'ezoic') {
    return <EzoicSmartAd 
      slot={slot} 
      style={style} 
      className={className} 
    />;
  }

  // No provider configured
  return fallbackContent ? <>{fallbackContent}</> : null;
};

/**
 * AdSense implementation of SmartAd
 */
const AdSenseSmartAd: React.FC<{
  slot: AdSlot;
  adFormat?: 'auto' | 'rectangle' | 'vertical' | 'horizontal';
  style?: React.CSSProperties;
  className?: string;
}> = ({ slot, adFormat = 'auto', style, className }) => {
  const { config: adConfig, isAdSenseLoaded } = useAdSense();

  // Show loading state while AdSense is loading
  if (!isAdSenseLoaded) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 rounded`} style={style}>
        <div className="text-gray-400 text-sm">Loading...</div>
      </div>
    );
  }

  // Get the appropriate ad slot
  const adSlot = adConfig.slots[slot];

  return (
    <AdSenseAd
      adSlot={adSlot}
      adClient={adConfig.adClient}
      adFormat={adFormat}
      style={style}
      className={className}
      fullWidthResponsive={true}
    />
  );
};

/**
 * Ezoic implementation of SmartAd
 */
const EzoicSmartAd: React.FC<{
  slot: AdSlot;
  style?: React.CSSProperties;
  className?: string;
}> = ({ slot, style, className }) => {
  const { config: ezoicConfig, isEzoicLoaded } = useEzoic();

  // Show loading state while Ezoic is loading
  if (!isEzoicLoaded) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 rounded`} style={style}>
        <div className="text-gray-400 text-sm">Loading...</div>
      </div>
    );
  }

  // Get the appropriate placement ID
  const placementId = ezoicConfig.placementIds[slot];

  return (
    <EzoicAd
      placementId={placementId}
      style={style}
      className={className}
    />
  );
};

export default SmartAd;
